import { create } from 'zustand';
import {
  safeFetch,
  transformToDashboardStats,
  safeGet,
  safeNumber,
  createFallbackDashboardData,
  safeTrialDays,
} from '../utils/safeData';
import { API_CONFIG, buildURL } from '../config/api';
import { api } from '../api/client';
import logger from '../utils/logger';
import { demoDashboard } from '../data/demoDashboard';

let dashboardRefreshTimer = null;

const TOP_PRODUCTS_PRIORITY = {
  realtime: 3,
  metrics: 2,
  fallback: 1,
};

const buildRevenueChartData = (trend, fallbackFactory) => {
  if (Array.isArray(trend) && trend.length > 0) {
    return trend.map((entry, index) => {
      const revenue = safeNumber(entry?.revenue, 0);
      const profitValue = entry?.profit != null ? entry.profit : revenue * 0.32;
      const profit = safeNumber(profitValue, Math.round(revenue * 0.32));
      const label = entry?.name
        || (entry?.date ? new Date(entry.date).toLocaleDateString('en-US', { weekday: 'short' }) : `Day ${index + 1}`);

      return {
        name: label,
        revenue: Math.round(revenue),
        profit: Math.round(profit),
      };
    });
  }

  return fallbackFactory();
};

const PLAN_CATALOG = {
  starter: {
    key: 'starter',
    name: 'Starter',
    price: 29,
    annual_price: 23,
    scans: 2500,
    api_calls: 25000,
    marketplaces: 20,
    team_members: 1,
    data_retention_days: 30,
    apps: 3,
    overage_fee: 0.10,
    description: 'Everything you need to get started with marketplace intelligence.',
    features: [
      '2,500 Product Scans/mo',
      '25,000 API Calls/mo',
      '20 Marketplaces',
      '3 App Access',
      '30-Day Data Retention',
      '1 User Seat',
      'Email Support',
      'Real-Time Alerts',
    ],
    cta: 'Get Started',
  },
  professional: {
    key: 'professional',
    name: 'Professional',
    price: 79,
    annual_price: 63,
    scans: 25000,
    api_calls: 250000,
    marketplaces: 83,
    team_members: 5,
    data_retention_days: 365,
    apps: -1,
    overage_fee: 0.05,
    description: 'Full platform access for growing teams and power sellers.',
    is_popular: true,
    features: [
      '25,000 Product Scans/mo',
      '250,000 API Calls/mo',
      'All 83+ Marketplaces',
      'All Apps Included',
      '1-Year Data Retention',
      'Up to 5 Users',
      'Priority Support',
      'Advanced Analytics',
      'API Access',
      'Custom Dashboards',
    ],
    cta: 'Start Professional',
  },
  enterprise: {
    key: 'enterprise',
    name: 'Enterprise',
    price: 199,
    annual_price: 159,
    scans: -1,
    api_calls: -1,
    marketplaces: 100,
    team_members: -1,
    data_retention_days: -1,
    apps: -1,
    overage_fee: 0,
    description: 'Dedicated infrastructure and support for large-scale operations.',
    custom_development: true,
    features: [
      'Unlimited Product Scans',
      'Unlimited API Calls',
      'All Marketplaces + Beta',
      'All Apps Included',
      'Unlimited Data Retention',
      'Unlimited Users',
      'Dedicated Account Manager',
      'Custom Integrations',
      'SLA Guarantee (99.99%)',
      'SSO / SAML Auth',
      'On-Premise Option',
    ],
    cta: 'Get Enterprise',
  },
};

// Backward-compatible aliases mapping old plan keys to new ones
const PLAN_ALIAS = {
  free: 'starter',
  couponer: 'starter',
  coupon_scout: 'starter',
  quantum_spark: 'starter',
  parttime: 'starter',
  weekend_warrior: 'starter',
  smart_seller: 'starter',
  pro: 'professional',
  pro_arbitrage: 'professional',
  neural_sync: 'professional',
  business: 'professional',
  quantum_pro: 'professional',
  business_pro: 'professional',
  singularity: 'enterprise',
  infinity_pro: 'enterprise',
  enterprise_scale: 'enterprise',
  unlimited_pro: 'enterprise',
};

const PLAN_DISPLAY_ORDER = ['starter', 'professional', 'enterprise'];

const useDashboardStore = create((set, get) => ({
  // State
  stats: {
    revenue: 0,
    monthlyProfit: 0,
    productsAnalyzed: 0,
    aiPerformance: 0,
  },
  topProducts: [],
  products: [],
  productsTotal: 0,
  productsPage: 1,
  productsLimit: 20,
  productsLoading: false,
  productsError: null,
  chartData: [],
  loading: false,
  error: null,
  fallback: false,
  demoMode: false,
  demoProducts: [],
  lastMetricsUpdate: null,
  topProductsMeta: {
    source: null,
    updatedAt: null,
  },

  // User state
  currentUser: {
    plan: 'professional',
    usage: {
      scans: 480,
      api_calls: 18200,
    },
    trial_days_left: null,
  },

  // Safe getter for trial days display
  getTrialDaysDisplay: () => {
    const { currentUser } = get();
    return safeTrialDays(currentUser.trial_days_left);
  },

  // Plan catalog and helper functions
  planLimits: PLAN_CATALOG,
  planDisplayOrder: PLAN_DISPLAY_ORDER,
  resolvePlanKey: (planKey) => PLAN_ALIAS[planKey] || planKey,
  getPlanConfig: (planKey) => {
    const resolved = PLAN_ALIAS[planKey] || planKey;
    return PLAN_CATALOG[resolved] || PLAN_CATALOG.starter;
  },

  // Actions
  fetchDashboardStats: async () => {
    if (get().demoMode) {
      return;
    }
    set({ loading: true, error: null, fallback: false });

    try {
      const metrics = await api.metrics();
      get().updateFromMetrics(metrics, { fallback: false });
      set({
        loading: false,
        error: null,
        lastMetricsUpdate: new Date().toISOString(),
      });
    } catch (error) {
      logger.error('Error fetching dashboard stats:', error);
      set({
        loading: false,
        error: error?.message || 'Failed to load dashboard metrics',
      });
    }
  },

	  searchProducts: async (query, options = {}) => {
	    if (get().demoMode) {
	      const allProducts = get().demoProducts.length ? get().demoProducts : demoDashboard.products;
	      const normalized = (query || '').toLowerCase();
	      const shouldShowAll = !normalized || normalized === 'trending' || normalized === 'all';
	      const filtered = shouldShowAll
	        ? allProducts
	        : normalized
	        ? allProducts.filter((product) => `${product.title ?? ''} ${product.marketplace ?? ''}`.toLowerCase().includes(normalized))
	        : allProducts;

      set({
        products: filtered,
        productsLoading: false,
        productsError: null,
        productsPage: 1,
        productsTotal: filtered.length,
      });

      return filtered;
    }

    const page = options.page ?? get().productsPage;
    const limit = options.limit ?? get().productsLimit;
    const filters = options.filters ?? undefined;
    const useLiveData = import.meta.env?.VITE_USE_LIVE_DATA === 'true';

    set({ productsLoading: true, productsError: null, productsPage: page, productsLimit: limit });

    try {
      // Check if query looks like an ASIN and we have live data enabled
      const asinPattern = /^B[0-9A-Z]{9}$/;
      if (useLiveData && asinPattern.test(query)) {
        logger.debug('ASIN detected, using marketplace search:', query);
        const result = await api.marketplaceSearch(query);
        const products = safeGet(result, 'products', []);
        const total = products.length;
        set({
          products,
          productsTotal: total,
          productsLoading: false,
          productsError: null
        });
        return;
      }

      // Fallback to regular product search
      const result = await api.productsSearch(query, { limit, filters });
      const products = safeGet(result, 'products', []);
      const total = safeGet(result, 'total', products.length);
      set({ products, productsTotal: total, productsLoading: false, productsError: null });
    } catch (error) {
      logger.error('Error searching products:', error);
      const errorMessage = error?.message || 'Failed to load products.';
      set({ products: [], productsTotal: 0, productsLoading: false, productsError: errorMessage });
    }
  },

  // Scan a product by barcode (EAN/UPC)
  scanByBarcode: async (barcodeOrPayload) => {
    set({ productsLoading: true, productsError: null });
    try {
      const result = await api.barcodeScan(barcodeOrPayload);
      const products = Array.isArray(result?.products)
        ? result.products
        : result?.product
          ? [result.product]
          : [];
      set({ products, productsLoading: false, productsError: null });
    } catch (error) {
      logger.error('Error scanning barcode:', error);
      set({ products: [], productsLoading: false, productsError: 'Barcode scan failed' });
    }
  },

  fetchTopProducts: async () => {
    if (get().demoMode) {
      return;
    }
    try {
      // Use dashboard metrics top_products when available
      const metrics = await api.metrics();
      const products = safeGet(metrics, 'top_products', []);
      if (Array.isArray(products) && products.length > 0) {
        get().updateTopProducts(products, {
          source: 'metrics',
          updatedAt: new Date().toISOString(),
        });
        return;
      }
      // Fallback to a generic search if metrics has no top products
      await get().searchProducts('electronics');
      const fallbackProducts = get().products;
      if (Array.isArray(fallbackProducts) && fallbackProducts.length > 0) {
        get().updateTopProducts(fallbackProducts, {
          source: 'fallback',
          updatedAt: new Date().toISOString(),
        });
      }
    } catch (error) {
      await get().searchProducts('electronics');
      const fallbackProducts = get().products;
      if (Array.isArray(fallbackProducts) && fallbackProducts.length > 0) {
        get().updateTopProducts(fallbackProducts, {
          source: 'fallback',
          updatedAt: new Date().toISOString(),
        });
      }
    }
  },

  generateChartData: (baseRevenue) => {
    // Generate realistic revenue data for the last 7 days with safe values
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const safeBaseRevenue = safeNumber(baseRevenue, 0);

    return days.map((day, index) => ({
      name: day,
      revenue: Math.round(safeBaseRevenue * (0.8 + Math.random() * 0.4)),
      profit: Math.round(safeBaseRevenue * 0.32 * (0.7 + Math.random() * 0.5)),
    }));
  },

  updateFromMetrics: (metrics = {}, options = {}) => {
    const transformedStats = transformToDashboardStats(metrics);
    const chartData = buildRevenueChartData(
      safeGet(metrics, 'revenue_trend', []),
      () => get().generateChartData(transformedStats.revenue),
    );

    set((state) => {
      const timestamp = new Date().toISOString();
      const nextState = {
        stats: transformedStats,
        chartData,
        loading: false,
        error: null,
        fallback: Boolean(options.fallback),
        lastMetricsUpdate: timestamp,
      };

      if (Array.isArray(metrics.top_products) && metrics.top_products.length > 0) {
        const applied = state.applyTopProducts(metrics.top_products, {
          source: options.fallback ? 'fallback' : 'metrics',
          updatedAt: timestamp,
        });

        if (applied.shouldUpdate) {
          nextState.topProducts = applied.products;
          nextState.topProductsMeta = applied.meta;
        }
      }

      return nextState;
    });
  },

  updateRevenueSeries: (payload) => {
    const revenueTrend = Array.isArray(payload?.revenue_trend)
      ? payload.revenue_trend
      : Array.isArray(payload)
        ? payload
        : [];

    if (!revenueTrend.length) {
      return;
    }

    const chartData = buildRevenueChartData(
      revenueTrend,
      () => get().chartData.length ? get().chartData : get().generateChartData(get().stats.revenue),
    );

    set({ chartData, lastMetricsUpdate: new Date().toISOString() });
  },

  applyTopProducts: (products, meta = {}) => {
    if (!Array.isArray(products) || !products.length) {
      return { shouldUpdate: false, products: products ?? [], meta: meta ?? {} };
    }

    const state = get();
    const currentMeta = state.topProductsMeta || {};
    const currentProducts = state.topProducts || [];
    const nextMeta = {
      source: meta.source || 'realtime',
      updatedAt: meta.updatedAt || new Date().toISOString(),
    };

    const currentPriority = TOP_PRODUCTS_PRIORITY[currentMeta.source] || 0;
    const nextPriority = TOP_PRODUCTS_PRIORITY[nextMeta.source] || 0;

    let shouldUpdate = false;

    if (nextPriority > currentPriority) {
      shouldUpdate = true;
    } else if (nextPriority === currentPriority) {
      const currentTime = currentMeta.updatedAt ? Date.parse(currentMeta.updatedAt) : 0;
      const nextTime = Date.parse(nextMeta.updatedAt);
      shouldUpdate = nextTime >= currentTime;
    }

    if (shouldUpdate) {
      return {
        shouldUpdate: true,
        products,
        meta: nextMeta,
      };
    }

    return { shouldUpdate: false, products: currentProducts, meta: currentMeta };
  },

  updateTopProducts: (products, meta = {}) => {
    const applied = get().applyTopProducts(products, meta);
    if (!applied.shouldUpdate) {
      return;
    }

    set({ topProducts: applied.products, topProductsMeta: applied.meta });
  },

  updateUsage: (type, amount) => {
    const currentUser = get().currentUser;
    set({
      currentUser: {
        ...currentUser,
        usage: {
          ...currentUser.usage,
          [type]: currentUser.usage[type] + amount,
        },
      },
    });
  },

  checkUsageLimits: () => {
    const { currentUser, getPlanConfig } = get();
    const limits = getPlanConfig(currentUser.plan);
    const usage = currentUser.usage;
    const scanCap = limits.scans > 0 ? limits.scans : Infinity;
    const apiCap = limits.api_calls > 0 ? limits.api_calls : Infinity;

    const scanPercentage = scanCap === Infinity ? 0 : (usage.scans / scanCap) * 100;
    const apiPercentage = apiCap === Infinity ? 0 : (usage.api_calls / apiCap) * 100;

    return {
      scans: {
        percentage: scanPercentage,
        isNearLimit: scanCap === Infinity ? false : scanPercentage > 90,
        isOverLimit: scanCap === Infinity ? false : scanPercentage >= 100,
      },
      api_calls: {
        percentage: apiPercentage,
        isNearLimit: apiCap === Infinity ? false : apiPercentage > 90,
        isOverLimit: apiCap === Infinity ? false : apiPercentage >= 100,
      },
    };
  },

  // Initialize data on mount
  initialize: () => {
    set({ demoMode: false });
    get().fetchDashboardStats();
    get().fetchTopProducts();

    if (dashboardRefreshTimer) {
      clearInterval(dashboardRefreshTimer);
    }

    dashboardRefreshTimer = setInterval(() => {
      get().fetchDashboardStats();
    }, 30000);
  },

  // Cleanup method
  cleanup: () => {
    if (dashboardRefreshTimer) {
      clearInterval(dashboardRefreshTimer);
      dashboardRefreshTimer = null;
    }
  },

  loadDemoData: () => {
    if (dashboardRefreshTimer) {
      clearInterval(dashboardRefreshTimer);
      dashboardRefreshTimer = null;
    }

    set({
      stats: demoDashboard.stats,
      chartData: demoDashboard.chartData,
      products: demoDashboard.products,
      productsTotal: demoDashboard.products.length,
      topProducts: demoDashboard.topProducts,
      topProductsMeta: {
        source: 'demo',
        updatedAt: new Date().toISOString(),
      },
      loading: false,
      error: null,
      fallback: true,
      demoMode: true,
      demoProducts: demoDashboard.products,
      currentUser: {
        name: demoDashboard.user?.name || 'Demo Seller',
        plan: demoDashboard.user?.plan || 'pro',
        usage: demoDashboard.user?.usage || { scans: 0, api_calls: 0 },
        trial_days_left: null,
      },
      lastMetricsUpdate: new Date().toISOString(),
    });
  },
}));

export default useDashboardStore;
